import React, { useState } from 'react';
import { DndContext, useSensor, useSensors, PointerSensor, closestCenter } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToParentElement } from '@dnd-kit/modifiers';
import MUIDataTable from 'mui-datatables';
import { TableRow, TableCell } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import PropTypes from 'prop-types';
import styles from './CPTValues.module.scss';

// Enhanced SortableRow Component
const SortableRow = ({ children, id, isListStart, disabled = false, activeId, isCollapsed, isDragOverlay = false, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style = {
    transform: isDragOverlay ? undefined : CSS.Transform.toString(transform),
    transition: isDragOverlay ? undefined : transition,
    opacity: isDragging && !isDragOverlay ? 0.5 : 1,
  };
  const dragOverlayStyle = isDragOverlay ? {
    height: '60px',
    minHeight: '60px',
    maxHeight: '60px',
    backgroundColor: 'white',
    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
    borderRadius: '4px',
    border: '2px solid #1976d2',
    zIndex: 9999,
  } : {};
  if (!isListStart && isCollapsed && !isDragOverlay) {
    return null;
  }
  return (
    <TableRow 
      ref={setNodeRef} 
      style={{ ...style, ...dragOverlayStyle }} 
      {...props}
    >
      <TableCell
        style={{
          cursor: isListStart && !disabled ? 'grab' : 'default',
          padding: '8px',
          width: '40px',
          backgroundColor: 'transparent',
          borderBottom: isDragOverlay ? 'none' : '1px solid rgba(224, 224, 224, 1)',
        }}
        {...(isListStart && !disabled ? attributes : {})}
        {...(isListStart && !disabled ? listeners : {})}
      >
        {isListStart ? <DragIndicatorIcon className={styles.lightDragIcon} /> : null}
      </TableCell>
      {children}
    </TableRow>
  );
};
SortableRow.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string.isRequired,
  isListStart: PropTypes.bool,
  disabled: PropTypes.bool,
  isDragOverlay: PropTypes.bool,
  activeId: PropTypes.string,
  isCollapsed: PropTypes.bool,
};

// Enhanced collision detection for precise cursor alignment
function enhancedCollisionDetection(args) {
  const { droppableContainers, pointerCoordinates } = args;
  if (!pointerCoordinates) {
    return closestCenter(args);
  }
  const centerY = pointerCoordinates.y;
  let closestDistance = Infinity;
  let closestDroppable = null;
  for (const droppable of droppableContainers) {
    const node = droppable.node.current;
    if (!droppable.rect.current || !node) continue;
    if (node.hasAttribute('data-collapsed-child')) continue;
    const rect = droppable.rect.current;
    const droppableCenterY = rect.top + rect.height / 2;
    const distance = Math.abs(centerY - droppableCenterY);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestDroppable = droppable;
    }
  }
  return closestDroppable ? [{ id: closestDroppable.id }] : [];
}

// Enhanced customRowRender function
function customRowRender({ row, dataIndex, rowIndex, cpt_codes, has_subscriptions, listGroups, tableColumns, addToList, deleteCPTCodes, styles, collapsedGroups, activeId, ...rowProps }) {
  const currentListNumber = cpt_codes[dataIndex]?.list;
  const isListStart = dataIndex === 0 || cpt_codes[dataIndex - 1]?.list !== currentListNumber;
  const listGroup = listGroups.find(group => group.listNumber === currentListNumber);
  const sortableId = listGroup ? listGroup.id : `list-${currentListNumber}`;
  const isCollapsed = collapsedGroups?.[currentListNumber];
  return (
    <SortableRow
      key={`row-${dataIndex}`}
      id={sortableId}
      isListStart={isListStart}
      disabled={has_subscriptions}
      isCollapsed={isCollapsed}
      activeId={activeId}
      listNumber={currentListNumber}
      rowIndex={dataIndex}
      {...rowProps}
    >
      {tableColumns.slice(1).map((col) => {
        let cellContent;
        if (col.options?.customBodyRender) {
          cellContent = col.options.customBodyRender(row[col.name], { rowIndex: dataIndex, rowData: row });
        } else if (typeof row[col.name] === 'object' && row[col.name] !== null) {
          cellContent = '';
        } else {
          cellContent = row[col.name];
        }
        return (
          <TableCell key={col.name} style={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
            {cellContent}
          </TableCell>
        );
      })}
    </SortableRow>
  );
}

const CPTDnDTable = ({
  cpt_codes,
  tableColumns,
  dispatchCPTValues,
  has_subscriptions,
  collapsedGroups,
  listGroups,
  styles,
  addToList,
  deleteCPTCodes,
  onCollapseGroup,
  onExpandGroup,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      },
    })
  );
  const [activeId, setActiveId] = useState(null);

  // Handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    const activeGroup = listGroups.find(group => group.id === event.active.id);
    if (activeGroup) {
      onCollapseGroup(activeGroup.listNumber);
    }
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    const activeGroup = listGroups.find(group => group.id === active.id);
    if (activeGroup) {
      onExpandGroup(activeGroup.listNumber);
    }
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const activeGroupIndex = listGroups.findIndex(group => group.id === active.id);
    const overGroupIndex = listGroups.findIndex(group => group.id === over.id);
    if (activeGroupIndex === -1 || overGroupIndex === -1) return;
    const reorderedGroups = arrayMove(listGroups, activeGroupIndex, overGroupIndex);
    const newCptCodes = reorderedGroups.flatMap((group, groupIndex) => 
      group.rows.map(row => {
        const { originalIndex, ...code } = row;
        return {
          ...code,
          list: groupIndex + 1 // Renumber lists starting from 1
        };
      })
    );
    dispatchCPTValues(newCptCodes);
  };

  const optionsWithCollapse = {
    filter: false,
    sort: false,
    selectableRows: 'none',
    responsive: 'standard',
    print: false,
    pagination: false,
    search: false,
    download: false,
    viewColumns: false,
    elevation: 0,
    customRowRender: (row, dataIndex, rowIndex) => {
      const currentListNumber = cpt_codes[dataIndex]?.list;
      const isListStart = dataIndex === 0 || cpt_codes[dataIndex - 1]?.list !== currentListNumber;
      const isCollapsed = collapsedGroups?.[currentListNumber];
      const rowProps = !isListStart && isCollapsed ? { 'data-collapsed-child': true } : {};
      return customRowRender({
        row: cpt_codes[dataIndex],
        dataIndex,
        rowIndex,
        cpt_codes,
        has_subscriptions,
        listGroups,
        tableColumns,
        addToList,
        deleteCPTCodes,
        styles,
        collapsedGroups,
        activeId,
        ...rowProps,
      });
    },
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={enhancedCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement]}
    >
      <SortableContext
        items={listGroups?.map(group => group.id)}
        strategy={verticalListSortingStrategy}
      >
        <div id="cpt-codes-table">
          <MUIDataTable
            data={cpt_codes}
            columns={tableColumns}
            options={optionsWithCollapse}
          />
        </div>
      </SortableContext>
    </DndContext>
  );
};

CPTDnDTable.propTypes = {
  cpt_codes: PropTypes.arrayOf(
    PropTypes.shape({
      list: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      // add other properties as needed
    })
  ).isRequired,
  tableColumns: PropTypes.array.isRequired,
  dispatchCPTValues: PropTypes.func.isRequired,
  has_subscriptions: PropTypes.bool,
  collapsedGroups: PropTypes.object,
  listGroups: PropTypes.array.isRequired,
  styles: PropTypes.object,
  addToList: PropTypes.func,
  deleteCPTCodes: PropTypes.func,
  onCollapseGroup: PropTypes.func.isRequired,
  onExpandGroup: PropTypes.func.isRequired,
};

export { SortableRow };
export default CPTDnDTable; 